import React, { useCallback, useEffect, useState } from "react";
import cn from "classnames";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalProduct.module.sass";
import Icon from "../Icon";
import TextInput from "../TextInput";
import Dropdown from "../Dropdown";
import Card from "../Card";
import { formatDateForInput } from "../../services/date.service";
import { generateRupture } from "../../services/contract.service";
import { showSuccess, showError } from "../../services/toast.service";
import Switch from "../Switch";


const ModalRupture = ({ visible, onClose, isFirstConnexion = false, folderId }) => {
  const escFunction = useCallback(
    (e) => {
      if (e.keyCode === 27 && !isFirstConnexion) {
        onClose();
      }
    },
    [onClose, isFirstConnexion]
  );
  const [motifRuptureSelected, setMotifRuptureSelected] = useState("");
  const [dateRupture, setDateRupture] = useState("");
  const [isGenerationInProgress, setIsGenerationInProgress] = useState(false);

  const motifRupture = [
    "Unilatérale de l'employeur ou de l'apprenant pendant les 45 premiers jours en emploi, consécutifs ou non",
    "D'un commun accord",
    "En cas de force majeure",
    "En cas de faute grave de l'apprenant",
    "En cas d'inaptitude de l'apprenant constatée par le médecin du travail",
    "En cas de décès de l'employeur maître d'apprentissage dans le cadre d'une entreprise universelle",
    "À l'initiative de l'apprenant après respect d'un préavis et sollicitation préalable du médiateur consulaire",
    "En cas de liquidation judiciaire de l'employeur sans maintien de l'activité",
    "En cas d'exclusion définitive de l'apprenant par le CFA",
    "En cas d'obtention du diplôme ou du titre de l'enseignement technologique préparé par l'apprenant",
    "Par décision administrative",
    "Autre"
  ];

  const [continueFormationCFA, setContinueFormationCFA] = useState(false);

  const handleContinueFormationChange = (name, value) => {
    if (name === 'continueFormation') {
      setContinueFormationCFA(value);
    }
  };
  
  const handleGenerateRuptureDocument = async () => {
    if (!motifRuptureSelected) {
      showError("Veuillez sélectionner un motif de rupture");
      return;
    }
    
    try {
      setIsGenerationInProgress(true);
      
      const ruptureData = {
        folderId: folderId,
        motifRupture: motifRuptureSelected,
        dateRupture: dateRupture,
        continuationFormation: continueFormationCFA
      };
      
      const response = await generateRupture(ruptureData);
      
      if (response && response.success) {
        showSuccess("Document de rupture généré avec succès");
        setTimeout(() => onClose(), 500);
      } else {
        throw new Error('Échec de génération du document');
      }
    } catch (error) {
      console.error('Erreur lors de la génération du document de rupture:', error);
      showError("Erreur lors de la génération du document de rupture");
    } finally {
      setIsGenerationInProgress(false);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    if (visible) {
      const target = document.querySelector("#modal-product");
      disableBodyScroll(target);
    } else {
      clearAllBodyScrollLocks();
    }
  }, [visible]);

  return createPortal(
    visible && (
      <div id="modal-product" className={styles.modal} onClick={isFirstConnexion ? null : undefined}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose}>
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer} style={{ position: 'relative' }}>
          <Card
            className={cn(styles.card)}
            title="Rupture de contrat"
            classTitle="title-green"
            style={{ minHeight: '600px', position: 'relative' }}
          >
            <div className={styles.description}>
              <div className={styles.field} style={{ position: 'relative', zIndex: 9999 }}>
                <Dropdown
                  className={styles.dropdown}
                  label="Motif de rupture"
                  name="motifRupture"
                  type="text"
                  required
                  options={motifRupture}
                  setValue={setMotifRuptureSelected}
                  value={motifRuptureSelected}
                  dropdownListStyles={{ position: 'absolute', zIndex: 9999, maxHeight: '300px', overflow: 'auto' }}
                />
              </div>
              
              <TextInput
                className={styles.field}
                label="Date de rupture"
                name="dateRupture"
                id="dateRupture"
                type="date"
                value={formatDateForInput(dateRupture)}
                onChange={(e) => {
                  // Récupérer uniquement la valeur du champ (pas l'événement complet)
                  const dateValue = typeof e === 'object' && e.target ? e.target.value : e;
                  setDateRupture(dateValue);
                }}
              />
              
              <div className={styles.field} style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginLeft: '10px' }}>L'apprenti poursuit-il sa formation en CFA après la rupture de son contrat d'apprentissage ?</span>
                <Switch
                  name="continueFormation"
                  value={continueFormationCFA}
                  onChange={() => handleContinueFormationChange('continueFormation', !continueFormationCFA)}
                  className={styles.switch}
                />
              </div>
              
              
              <div className={styles.btnWrap}>
                <button 
                  className="button-green"
                  onClick={handleGenerateRuptureDocument}
                  disabled={isGenerationInProgress || !motifRuptureSelected}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isGenerationInProgress ? (
                    <>
                      <Icon name="download" size="16" />
                      Génération en cours...
                    </>
                  ) : (
                    <>
                      <Icon name="download" size="16" />
                      Générer formulaire de rupture
                    </>
                  )}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    ),
    document.body
  );
};

export default ModalRupture;
